/**
 * Default pagination settings
 */
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 25,
  MAX_LIMIT: 100,
  MAX_LIMIT_LIGHTWEIGHT: 150, // Higher limit for lightweight queries (id + name only)
} as const;

/**
 * Get the appropriate max limit based on lightweight flag
 */
export function getMaxLimit(lightweight: boolean): number {
  return lightweight ? PAGINATION.MAX_LIMIT_LIGHTWEIGHT : PAGINATION.MAX_LIMIT;
}

/**
 * Normalize pagination params with defaults and limits
 */
export function normalizePagination(params: {
  page?: number;
  limit?: number;
  lightweight?: boolean;
}): {
  page: number;
  limit: number;
  skip: number;
} {
  const page = Math.max(1, params.page ?? PAGINATION.DEFAULT_PAGE);
  const maxLimit = getMaxLimit(params.lightweight ?? false);
  const limit = Math.min(maxLimit, Math.max(1, params.limit ?? PAGINATION.DEFAULT_LIMIT));
  const skip = (page - 1) * limit;

  return { page, limit, skip };
}

/**
 * Calculate pagination metadata
 */
export function calculatePaginationMeta(
  page: number,
  limit: number,
  total: number
): { page: number; limit: number; total: number; totalPages: number } {
  return {
    page,
    limit,
    total,
    totalPages: Math.ceil(total / limit),
  };
}

/**
 * Result interface for paginated queries
 */
export interface PaginatedResult<T> {
  items: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

/**
 * Execute a paginated query
 * Handles normalization, count, and query execution
 *
 * @example
 * ```typescript
 * const result = await paginatedQuery({
 *   page: 1,
 *   limit: 25,
 * }, async (skip, limit) => {
 *   const [total, items] = await Promise.all([
 *     db.count({ where }),
 *     db.findMany({ where, skip, take: limit }),
 *   ]);
 *   return { items, total };
 * });
 * ```
 */
export async function paginatedQuery<T>(
  params: { page?: number; limit?: number; lightweight?: boolean },
  queryFn: (skip: number, limit: number) => Promise<{ items: T[]; total: number }>
): Promise<PaginatedResult<T>> {
  const { page, limit, skip } = normalizePagination(params);

  const { items, total } = await queryFn(skip, limit);

  return {
    items,
    pagination: calculatePaginationMeta(page, limit, total),
  };
}

/**
 * Get Prisma select object for lightweight queries
 * Returns { id: true, name: true } when lightweight, undefined otherwise
 *
 * @param lightweight - Whether this is a lightweight query
 * @param nameField - The field to use for display name (defaults to 'name')
 * @returns Prisma select object or undefined
 */
export function getLightweightSelect(
  lightweight: boolean,
  nameField: string = 'name'
): { id: true; [key: string]: true } | undefined {
  if (!lightweight) return undefined;

  return {
    id: true,
    [nameField]: true,
  };
}
